import "./ShiftEditor.scss";

import { useMemo } from "react";

import { Box, darken, Grid, Stack } from "@mui/material";

import { useAppFlags } from "@/common/hooks";
import { white } from "@/common/theming";

import { CustomButton } from "../TrackedComponents";

import { ShiftEditor } from "./components";
import { DeprecatedShiftEditor } from "./DeprecatedShiftEditor";
import { TShiftEditorProps } from "./types";

export const FullShiftEditor = (props: TShiftEditorProps) => {
  const { staffJobOnShift } = useAppFlags();

  if (staffJobOnShift) return <ShiftEditorV2 {...props} />;
  return <DeprecatedShiftEditor {...props} />;
};

export const ShiftEditorV2 = (props: TShiftEditorProps) => {
  const {
    staffShift,
    deleteStaffShift,
    readonly,
    availableShiftTypes,
    isLastEdited = false,
  } = props;

  // shift type selector
  const backgroundColor = useMemo(
    () =>
      (staffShift.shiftTypeKey &&
        availableShiftTypes?.find(({ key }) => key === staffShift.shiftTypeKey)
          ?.scheduleViewColor) ||
      white,
    [availableShiftTypes, staffShift.shiftTypeKey],
  );

  const classNames = (() => {
    const names = ["component-shift-editor", "v2"];
    if (isLastEdited) names.push("is-last-edited");
    return names.join(" ");
  })();

  if (!backgroundColor) return null;

  return (
    <Grid
      container
      className={classNames}
      data-background-color={darken(backgroundColor, 0.2)}
      style={{ "--background-color": darken(backgroundColor, 0.2) } as React.CSSProperties}
    >
      <ShiftEditor.DebugTooltip {...props} />
      <ShiftEditor.ShiftHistory {...props} />

      <ShiftEditor.Section title="Shift Details" className="date-and-time-section">
        <Stack direction="row">
          <ShiftEditor.ShiftTypePicker {...props} />
          <ShiftEditor.DatePicker {...props} />
        </Stack>
        <ShiftEditor.TimePickers {...props} />

        <ShiftEditor.UnitPicker {...props} />
        <Stack direction="row">
          <ShiftEditor.StaffJobPicker {...props} />
          <ShiftEditor.PositionPicker {...props} />
        </Stack>
        <Stack direction="row">
          <ShiftEditor.StatusPicker {...props} />
          <ShiftEditor.IncentiveLevelPicker {...props} />
        </Stack>
      </ShiftEditor.Section>

      {!readonly && (
        <Stack direction="row">
          <Box flexGrow={1} /> {/* Spacer */}
          <CustomButton
            className="delete-shift-button"
            label="Delete"
            color="error"
            variant="outlined"
            onClick={() => deleteStaffShift(staffShift)}
          />
        </Stack>
      )}
    </Grid>
  );
};
